import React, { ChangeEvent, FC, useMemo } from "react";
import { PropertyWrapper } from "./PropertyWrapper";
import {
  Input,
  Slider,
  Stack,
  Typography,
  InputBaseProps,
} from "@mui/material";
import { isRangeDto, RangeDto } from "shared-ts-utils-core";
import { PropertyEditBaseProps } from "./PropertyEditBaseProps";
import { usePropertyEditBase } from "./usePropertyEditBase";
import { RemoveButton } from "./RemoveButton";
import { InputBaseComponentProps } from "@mui/material";
import { merge } from "lodash";

export interface SliderComponentProps {
  min?: number;
  max?: number;
  step?: number | null;
  inputProps?: InputBaseComponentProps;
}

export interface PropertyEditSliderProps extends PropertyEditBaseProps {
  value: RangeDto | number;
  unit?: string;
  componentProps?: SliderComponentProps;
}

export const PropertyEditSlider: FC<PropertyEditSliderProps> = (props) => {
  const { removable, handleRemove } = usePropertyEditBase(props);
  const { name, value, unit } = props;
  const valueAsArrayOrNumber = useMemo(() => {
    if (isRangeDto(value)) {
      return [value.min, value.max];
    } else {
      return value;
    }
  }, [value]);
  const getAriaValueText = (value: number): string => {
    return unit != null ? value + " " + unit : value.toString();
  };

  const handleChange = (event: Event, value: number | number[]) => {
    if (Array.isArray(value)) {
      props.onChange({ min: value[0], max: value[1] }, name);
    } else {
      props.onChange(value, name);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value === "" ? 0 : Number(event.target.value);
    props.onChange(newValue, name);
  };
  const handleInputBlur: InputBaseProps["onBlur"] = (event) => {
    if (typeof value === "number") {
      if (
        props.componentProps?.min != null &&
        value < props.componentProps?.min
      ) {
        props.onChange(props.componentProps?.min, name);
      } else if (
        props.componentProps?.max != null &&
        value > props.componentProps?.max
      ) {
        props.onChange(props.componentProps?.max, name);
      }
    }
    props.onBlur?.(event);
  };

  return (
    <PropertyWrapper
      label={props.label}
      size={props.size}
      fullWidth={props.fullWidth}
      helperText={props.helperText}
      error={props.error}
      sx={props.sx}
    >
      <Stack
        direction={"row"}
        gap={2}
        useFlexGap
        sx={{
          ml: "14px",
          mr: "7px",
          flexWrap: "nowrap",
          flexGrow: 1,
          minWidth: 0,
          alignItems: "center",
        }}
      >
        <Slider
          value={valueAsArrayOrNumber}
          onChange={handleChange}
          onBlur={props.onBlur}
          size="small"
          getAriaValueText={getAriaValueText}
          {...props.componentProps}
        />
        <Stack
          direction={"row"}
          alignItems={"center"}
          flexWrap={"nowrap"}
          minWidth={"fit-content"}
        >
          {isRangeDto(value) && (
            <Typography variant={"caption"} lineHeight={1.2} noWrap>
              {value.min} - {value.max}
            </Typography>
          )}
          {typeof value === "number" && (
            <Input
              value={value}
              sx={{
                minWidth: "fit-content",
                "&::before": { borderBottom: "unset !important" },
                input: { textAlign: "right" },
              }}
              onChange={handleInputChange}
              size="small"
              onBlur={handleInputBlur}
              inputProps={merge(
                {
                  step: props?.componentProps?.step,
                  min: props?.componentProps?.min,
                  max: props?.componentProps?.max,
                  type: "number",
                  tabIndex: -1,
                  "aria-labelledby": "input-slider",
                },
                props.componentProps?.inputProps
              )}
            />
          )}
          {unit != null && (
            <Typography sx={{ pl: 0.5, pr: 0.5, minWidth: "fit-content" }}>
              {unit}
            </Typography>
          )}
          {removable && <RemoveButton onRemove={handleRemove} />}
        </Stack>
      </Stack>
    </PropertyWrapper>
  );
};
